import { NoteDataSourceEnum, NoteDataSourceSchema } from '@eigtech/notes-types'
import { ContextualCan } from '@eigtech/ui-shared-auth'
import {
  Button,
  ButtonGroup,
  Checkbox,
  CheckboxGroup,
  FormControl,
  FormLabel,
  HStack,
  Heading,
  IconButton,
  Input,
  InputGroup,
  InputLeftAddon,
  InputRightElement,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Stack,
  Toolbar,
  Tooltip,
} from '@eigtech/ui-shared-dave'
import { getNoteDataSourceLabel } from '@eigtech/ui-shared-notes'
import { Dispatch, ReactNode, SetStateAction } from 'react'
import {
  HiOutlineSortAscending as AscIcon,
  HiOutlineSortDescending as DescIcon,
} from 'react-icons/hi'
import { MdAdd as AddNoteIcon, MdClose as ClearIcon, MdSearch as SearchIcon } from 'react-icons/md'

export type NotesToolbarProps = {
  query?: string
  setQuery?: Dispatch<SetStateAction<string>>
  isAscending?: boolean
  setIsAscending?: Dispatch<SetStateAction<boolean>>
  visibleNoteDataSources?: NoteDataSourceEnum[]
  setVisibleNoteDataSources?: Dispatch<SetStateAction<NoteDataSourceEnum[]>>
  createNote?: () => void
  heading?: ReactNode
  isPending?: boolean
  isError?: boolean
}

export function NotesToolbar({
  query,
  setQuery,
  isAscending,
  setIsAscending,
  visibleNoteDataSources,
  setVisibleNoteDataSources,
  createNote,
  heading = 'Notes',
  isPending,
}: NotesToolbarProps) {
  return (
    <Toolbar>
      <Stack
        alignItems={{ base: undefined, md: 'center' }}
        direction={{ base: 'column', md: 'row' }}
        justifyContent="space-between"
      >
        {typeof heading === 'string' ? (
          <Heading as="h3" size="md">
            Notes
          </Heading>
        ) : (
          heading
        )}

        <Stack direction={{ base: 'column', md: 'row' }}>
          {!!setQuery && (
            <InputGroup>
              <InputLeftAddon bg="gray.300">
                <SearchIcon />
              </InputLeftAddon>
              <Input
                bgColor="white"
                placeholder="Type to search notes..."
                value={query}
                onChange={(event) => setQuery(event.target.value ?? '')}
              />
              <InputRightElement>
                <IconButton
                  aria-label="Clear search."
                  h="6"
                  icon={<ClearIcon />}
                  minW="6"
                  variant="ghost"
                  onClick={() => setQuery('')}
                />
              </InputRightElement>
            </InputGroup>
          )}

          <HStack>
            {!!setVisibleNoteDataSources && (
              <Popover>
                <PopoverTrigger>
                  <Button variant="outline">Filter Notes</Button>
                </PopoverTrigger>
                <PopoverContent>
                  <PopoverArrow />
                  <PopoverCloseButton />
                  <PopoverHeader>Filter Notes</PopoverHeader>
                  <PopoverBody>
                    <FormControl>
                      <FormLabel>Data Sources</FormLabel>
                      <CheckboxGroup
                        value={visibleNoteDataSources}
                        onChange={(value) =>
                          setVisibleNoteDataSources(value as NoteDataSourceEnum[])
                        }
                      >
                        <Stack>
                          {NoteDataSourceSchema.options.map((value) => (
                            <Checkbox key={value} value={value}>
                              {getNoteDataSourceLabel(value)}
                            </Checkbox>
                          ))}
                        </Stack>
                      </CheckboxGroup>
                    </FormControl>
                  </PopoverBody>
                </PopoverContent>
              </Popover>
            )}

            <ButtonGroup isAttached isDisabled={isPending} variant="outline">
              {!!(setIsAscending && isAscending !== undefined) && (
                <Tooltip label={`Change sort to: ${isAscending ? 'descending' : 'ascending'}`}>
                  <IconButton
                    aria-label={`Change sort to: ${isAscending ? 'descending' : 'ascending'}.`}
                    data-sort-order={isAscending ? 'ascending' : 'descending'}
                    icon={isAscending ? <AscIcon /> : <DescIcon />}
                    isRound={false}
                    onClick={() => setIsAscending(!isAscending)}
                  />
                </Tooltip>
              )}

              <ContextualCan I="create" a="note">
                {!!createNote && (
                  <Tooltip label="Add note">
                    <IconButton
                      aria-label="Add note."
                      icon={<AddNoteIcon />}
                      isRound={false}
                      onClick={createNote}
                    />
                  </Tooltip>
                )}
              </ContextualCan>
            </ButtonGroup>
          </HStack>
        </Stack>
      </Stack>
    </Toolbar>
  )
}
